import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePortalNavigate, usePortalPath } from '@/hooks/usePortalPath';
import {
  actionAuthSubmitButtonClassName,
  actionFormInputClassName,
  actionLinkClassName,
} from '@core/theme/actionTheme.util';
import { AuthHeader } from '@/components/auth/AuthHeader';

const SignUp = () => {
  const portalNavigate = usePortalNavigate();
  const { path: portalPath } = usePortalPath();
  const { signUp } = useAuth();
  const [formState, setFormState] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await signUp({
        email: formState.email,
        password: formState.password,
        confirmPassword: formState.confirmPassword,
      });

      if (result.success) {
        portalNavigate('/confirm-signup', {
          state: {
            email: result.email,
            password: formState.password,
          },
        });
      }
    } catch (err) {
      console.error('Error signing up:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-white">
      <div className="w-full max-w-md px-6 py-8">
        <AuthHeader title="Sign up for an account" />

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className={actionFormInputClassName}
                placeholder="Enter your email"
                value={formState.email}
                onChange={(e) =>
                  setFormState({ ...formState, email: e.target.value })
                }
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className={actionFormInputClassName}
                placeholder="Enter your password"
                value={formState.password}
                onChange={(e) =>
                  setFormState({ ...formState, password: e.target.value })
                }
              />
            </div>
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                className={actionFormInputClassName}
                placeholder="Confirm password"
                value={formState.confirmPassword}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    confirmPassword: e.target.value,
                  })
                }
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`${actionAuthSubmitButtonClassName} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {isLoading ? 'Creating account...' : 'Sign up'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link
              to={portalPath('/login')}
              className={`font-medium ${actionLinkClassName}`}
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
